import { useAuth, useClerk, useOrganization, useOrganizationList } from '@clerk/clerk-react';
import { localizeUrl, safeUrl } from "@flobi/asset-library/notifications";
import { NotificationProvider } from '@flobi/asset-library/notifications';
import '@flobi/asset-library/share.css';
import '@flobi/asset-library/notifications.css';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { io } from 'socket.io-client';
import { useLocation, useNavigate } from 'react-router-dom';
import { inAppPath } from '../home/notificationLink';
import { gatewayCall } from '../share/gateway';
import { setTokenGetter } from '../services/apiClient';
import { ArtworkAccount } from '../account/ArtworkAccount';
import { touchSession } from '../auth/session';
import { useTheme } from '../theme';
import { HOME_URL } from '../homeUrl';
import { AppDirectoryLogo } from '../ui/AppDirectoryLogo';
import { useAppLogo } from '../lib/appDirectory';

const SOCKET_URL = import.meta.env.VITE_BACKEND_URL as string | undefined;
const PREFERENCES_URL = localizeUrl(`${HOME_URL}/settings?tab=notifications`) ?? "";

interface Workspace {
  id: string;
  name: string;
  clerkOrgId?: string;
}

/** The file being looked at, as the notification system names an object: `artwork:file:<id>`. */
export function fileThreadKey(pathname: string): string | undefined {
  const match = /^\/(?:f|file)\/([^/]+)/.exec(pathname);
  return match ? `artwork:file:${decodeURIComponent(match[1]!)}` : undefined;
}

/**
 * Where a row's address opens: an Artwork page in place (`path`), anything else by loading `href`. It is
 * checked first (the kit checks a row's address too; this is for any other caller): an address that is
 * not this page or a Flobi app opens Artwork's start page ("/") instead.
 */
export function rowTarget(url: string, here: string = window.location.origin): { path: string | null; href: string } {
  const href = safeUrl(url, { here, fallback: '/' });
  return { path: inAppPath(href, here), href };
}

/**
 * The one notification system, mounted once at the root over Home and the editor alike: the socket,
 * the banners, the Centre and its shortcut (N, or ⌘⇧.). `NotificationBell` goes wherever the chrome
 * wants one (Home's header, the editor's top bar); the editor shows nothing else of it.
 *
 * It stays asleep until there is a session and a workspace: the public view page and the labs, and
 * the moment before sign-in, neither read nor open anything.
 */
export function ArtworkNotifications({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { organization } = useOrganization();
  const { setActive } = useOrganizationList();
  const clerk = useClerk();
  const navigateTo = useNavigate();
  const { pathname } = useLocation();
  const { theme } = useTheme();
  const ready = isLoaded && !!isSignedIn && !!organization;

  // During render, as AuthGate does: the first calls must carry a token, on the public view page too.
  if (ready) setTokenGetter(() => getToken());

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  // Every app mark in the Centre and the banners: the App Directory's logo in the person's style on
  // the editor's theme (lib/appDirectory) — always an element, so with none the kit draws an empty
  // box rather than its letter tile.
  const logo = useAppLogo(theme === 'light' ? 'light' : 'dark');
  const appIcon = useCallback((slug: string) => <AppDirectoryLogo src={logo(slug)} />, [logo]);
  useEffect(() => {
    if (!ready) return;
    let live = true;
    // A notification names its workspace by the internal id; Clerk switches by its own. This list carries both.
    // `role` and `guest` stay: the "add it to a workspace" pickers offer only owner/admin/editor workspaces, never a guest one.
    gatewayCall<{ workspaces?: { id: string; name?: string; clerkOrgId?: string | null; role?: string | null; guest?: boolean }[] }>('users', 'sharing.workspaces.mine', {})
      .then(
        (res) =>
          live &&
          setWorkspaces(
            (res?.workspaces ?? []).map((w) => ({ id: w.id, name: w.name ?? '', clerkOrgId: w.clerkOrgId ?? undefined, role: w.role ?? null, guest: w.guest === true })),
          ),
      )
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [ready]);

  // The gateway call, awake only when there is somebody to ask for.
  const readyRef = useRef(ready);
  readyRef.current = ready;
  const http = useCallback(
    (service: string, action: string, data?: Record<string, unknown>, opts?: { signal?: AbortSignal; method?: 'get' | 'post' | 'patch' | 'delete' }) =>
      readyRef.current ? gatewayCall(service, action, data, opts) : Promise.reject(new Error('Not signed in')),
    [],
  );

  // Another workspace's row: Clerk switches, then the page loads afresh at the row's address (the
  // workspace lives in the token, and everything on screen belongs to the old one).
  const switched = useRef(false);
  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      const target = workspaces.find((w) => w.id === workspaceId || w.clerkOrgId === workspaceId);
      if (!target?.clerkOrgId || !setActive) throw new Error('Unknown workspace');
      await setActive({ organization: target.clerkOrgId });
      // setActive changes the workspace in this tab only; the touch saves it to the session before
      // anything reloads or goes Home, so the next load and every other Flobi app open it too, as
      // Home's switch does (Mohamad, 2026-10-07).
      await touchSession(clerk);
      switched.current = true;
    },
    [workspaces, setActive, clerk],
  );

  const navigate = useCallback(
    (url: string) => {
      const { path, href } = rowTarget(url);
      if (path && !switched.current) navigateTo(path);
      else window.location.assign(path ?? href);
    },
    [navigateTo],
  );

  const activeWorkspaceId = workspaces.find((w) => w.clerkOrgId === organization?.id)?.id;
  const openPreferences = useCallback(() => window.location.assign(PREFERENCES_URL), []);
  const getTokenNow = useCallback(() => getToken(), [getToken]);

  return (
    <NotificationProvider
      http={http}
      io={ready ? io : undefined}
      socketUrl={SOCKET_URL}
      getToken={getTokenNow}
      appSlug="artwork"
      theme="artwork"
      mode={theme === 'light' ? 'light' : 'dark'}
      appIcon={appIcon}
      workspaces={workspaces}
      activeWorkspaceId={activeWorkspaceId}
      switchWorkspace={switchWorkspace}
      navigate={navigate}
      activeThreadKey={fileThreadKey(pathname)}
      onOpenSettings={openPreferences}
      banners={ready}
      centre={ready}
      shortcut={ready ? 'n' : false}
      chordShortcut={ready}
    >
      {/* The account kit rides inside it: the same gateway call, workspaces and switch, one Centre behind the menu's row. */}
      <ArtworkAccount http={http} getToken={getTokenNow} workspaces={workspaces} activeWorkspaceId={activeWorkspaceId} switchWorkspace={switchWorkspace}>
        {children}
      </ArtworkAccount>
    </NotificationProvider>
  );
}
